import {
  DEFAULT_PAGE_TAB_VALUE,
  PAGE_TAB_VALUES,
  getPageTabValueForPathname,
  normalizePageTabValue,
  type PageTabValue,
} from '@constants/pageTabs';
import { getIframeQueryParamsFromSearch } from './queryParams';

export const HEIGHT_CHANGE_ACTION = 'heightchange' as const;
export const ASP_TAB_CHANGE_MESSAGE_TYPE = 'asp_tab_change' as const;
export const ASP_SECTION_JUMP_MESSAGE_TYPE = 'asp_section_jump' as const;

export const PERFORMANCE_SECTION_KEYS = ['annual', 'quarterly', 'balance', 'cashflow'] as const;
export type PerformanceSectionKey = (typeof PERFORMANCE_SECTION_KEYS)[number];

export const INVESTMENT_SECTION_KEYS = [
  'investment-basic', 'investment-consensus', 'investment-finance',
  'investment-stock-attributes', 'investment-technical', 'investment-technical-indicators',
] as const;
export type InvestmentSectionKey = (typeof INVESTMENT_SECTION_KEYS)[number];
export type SectionJumpTabId = 2 | 6;

interface SectionJumpBase {
  type: typeof ASP_SECTION_JUMP_MESSAGE_TYPE;
  /** CSS pixels from the child document's top, measured at activation time. */
  offsetTop: number;
}

export type SectionJumpMessage = SectionJumpBase & (
  | { tab_id: 2; section: PerformanceSectionKey }
  | { tab_id: 6; section: InvestmentSectionKey }
);

export const buildSectionJumpMessage = (
  section: string,
  offsetTop: number,
  tabId: SectionJumpTabId = 2,
): SectionJumpMessage | null => {
  if (!Number.isFinite(offsetTop) || offsetTop < 0) return null;

  if (tabId === 2 && PERFORMANCE_SECTION_KEYS.includes(section as PerformanceSectionKey)) {
    return { type: ASP_SECTION_JUMP_MESSAGE_TYPE, tab_id: 2,
      section: section as PerformanceSectionKey, offsetTop };
  }
  if (tabId === 6 && INVESTMENT_SECTION_KEYS.includes(section as InvestmentSectionKey)) {
    return { type: ASP_SECTION_JUMP_MESSAGE_TYPE, tab_id: 6,
      section: section as InvestmentSectionKey, offsetTop };
  }
  return null;
};

export type WidgetTabId = 1 | 2 | 3 | 4 | 5 | 6;

export interface WidgetLocationSnapshot {
  pathname: string;
  search: string;
}

export interface HeightChangeMessage {
  tab_id: WidgetTabId;
  action: typeof HEIGHT_CHANGE_ACTION;
  height: number;
}

export interface AspTabChangeMessage {
  type: typeof ASP_TAB_CHANGE_MESSAGE_TYPE;
  tab_id: WidgetTabId;
}

const TAB_ID_BY_PAGE_TAB_VALUE: Record<PageTabValue, WidgetTabId> = {
  [PAGE_TAB_VALUES.summary]: 1,
  [PAGE_TAB_VALUES.performanceTrends]: 2,
  [PAGE_TAB_VALUES.dividends]: 3,
  [PAGE_TAB_VALUES.comparison]: 4,
  [PAGE_TAB_VALUES.instructions]: 5,
  [PAGE_TAB_VALUES.investmentIndicators]: 6,
};

const toSafeHeight = (height: number): number =>
  Number.isFinite(height) && height > 0 ? Math.ceil(height) : 0;

export const getWidgetPageRoot = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('.widget-shell__content > *');

/**
 * Document-relative bottom edge of an element.
 *
 * `includeScrollExtent` is opt-in because the two element kinds differ:
 * - viewport-constrained wrappers (`html`, `body`, `#root`, `.widget-shell`,
 *   `.widget-shell__content`) are tied to the iframe viewport through
 *   `min-height: 100vh`, so their `scrollHeight` can stay inflated after a
 *   short page renders and must never drive the posted height;
 * - the page root (`.widget-shell__content > *`) has no viewport-height
 *   constraint, so its `scrollHeight` is the intrinsic content extent. Ignoring
 *   it (work_items_108) under-measured pages whose descendants overflow the page
 *   root border box, leaving the remaining pixels as an iframe scrollbar
 *   (work_items_111).
 */
const getElementBottom = (
  element: HTMLElement | null,
  includeScrollExtent = false,
): number => {
  if (!element) return 0;

  const rect = element.getBoundingClientRect();
  const scrollTop = window.scrollY || window.pageYOffset || 0;
  const elementTop = rect.top + scrollTop;
  const renderedHeight = Math.max(
    rect.height,
    element.offsetHeight,
    includeScrollExtent ? element.scrollHeight : 0,
  );

  return Math.max(
    rect.bottom + scrollTop,
    elementTop + renderedHeight,
  );
};

const getPageTabValueForLocation = (
  location: WidgetLocationSnapshot,
): PageTabValue => {
  const query = getIframeQueryParamsFromSearch(location.search);
  return (
    normalizePageTabValue(query.tab) ??
    getPageTabValueForPathname(location.pathname) ??
    DEFAULT_PAGE_TAB_VALUE
  );
};

export const getWidgetTabIdForLocation = (
  location: WidgetLocationSnapshot,
): WidgetTabId => TAB_ID_BY_PAGE_TAB_VALUE[getPageTabValueForLocation(location)];

export const getWidgetDocumentHeight = (): number => {
  const body = document.body;
  const html = document.documentElement;
  const widgetPageRoot = getWidgetPageRoot();
  // Page root only: include its intrinsic scroll extent so overflowing
  // descendants cannot leave residual pixels behind an iframe scrollbar.
  const widgetPageRootHeight = getElementBottom(widgetPageRoot, true);

  if (widgetPageRootHeight > 0) {
    return Math.ceil(widgetPageRootHeight);
  }

  const widgetContent = document.querySelector<HTMLElement>('.widget-shell__content');
  const widgetContentHeight = getElementBottom(widgetContent);
  if (widgetContentHeight > 0) {
    return Math.ceil(widgetContentHeight);
  }

  return Math.ceil(Math.max(getElementBottom(body), getElementBottom(html)));
};

export const buildHeightChangeMessage = (
  location: WidgetLocationSnapshot,
  height: number,
): HeightChangeMessage => ({
  tab_id: getWidgetTabIdForLocation(location),
  action: HEIGHT_CHANGE_ACTION,
  height: toSafeHeight(height),
});

export const buildAspTabChangeMessage = (
  location: WidgetLocationSnapshot,
): AspTabChangeMessage => ({
  type: ASP_TAB_CHANGE_MESSAGE_TYPE,
  tab_id: getWidgetTabIdForLocation(location),
});

export const getWidgetParentOrigin = (): string => {
  const configured = process.env.REACT_APP_WIDGET_PARENT_ORIGIN?.trim();
  if (configured) return configured;

  try {
    return document.referrer ? new URL(document.referrer).origin : '*';
  } catch {
    return '*';
  }
};

/** Returns false so the caller can retain native anchors outside a working iframe. */
export const postSectionJumpMessage = (
  section: string,
  tabId: SectionJumpTabId = 2,
): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!buildSectionJumpMessage(section, 0, tabId)) return false;
  const target = document.getElementById(section);
  if (!target) return false;

  const message = buildSectionJumpMessage(
    section,
    target.getBoundingClientRect().top + window.scrollY,
    tabId,
  );
  if (!message) return false;

  if (!window.parent || window.parent === window) {
    console.log('[eSmart postMessage] asp_section_jump preview (standalone; not sent)', message);
    return false;
  }

  try {
    window.parent.postMessage(message, getWidgetParentOrigin());
    console.log('[eSmart postMessage] asp_section_jump', message);
    return true;
  } catch {
    return false;
  }
};

export const postHeightChangeMessage = (
  location: WidgetLocationSnapshot,
  targetOrigin = getWidgetParentOrigin(),
): HeightChangeMessage | null => {
  if (typeof window === 'undefined' || window.parent === window) {
    return null;
  }

  const message = buildHeightChangeMessage(location, getWidgetDocumentHeight());
  window.parent.postMessage(message, targetOrigin);
  return message;
};
